import { defineStore } from 'pinia'
import customerService from '@/services/customer.service'
import type { Customer, CustomerFilters, CustomerFormData } from '@/types/customer.types'
import type { SortOrder } from '@/types/api.types'

interface CustomerState {
  customers: Customer[]
  selectedCustomer: Customer | null
  loading: boolean
  filters: CustomerFilters
  pagination: {
    page: number
    pageSize: number
    total: number
  }
}

export const useCustomerStore = defineStore('customers', {
  state: (): CustomerState => ({
    customers: [],
    selectedCustomer: null,
    loading: false,
    filters: { sort_by: 'created_at', sort_order: 'desc' },
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0
    }
  }),

  getters: {
    customerById: (state) => (id: string) => 
      state.customers.find(c => c.id === id)
  },

  actions: {
    async fetchCustomers() {
      this.loading = true
      try {
        const response = await customerService.getCustomers({
          ...this.filters,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        this.customers = response.data
        this.pagination.total = response.total
      } finally {
        this.loading = false
      }
    },

    async fetchCustomer(id: string) {
      this.loading = true
      try {
        this.selectedCustomer = await customerService.getCustomer(id)
        return this.selectedCustomer
      } finally {
        this.loading = false
      }
    },

    async createCustomer(data: CustomerFormData) {
      const customer = await customerService.createCustomer(data)
      this.customers.unshift(customer)
      return customer
    },

    async updateCustomer(id: string, data: Partial<CustomerFormData>) {
      const updated = await customerService.updateCustomer(id, data)
      const index = this.customers.findIndex(c => c.id === id)
      if (index !== -1) {
        this.customers[index] = updated
      }
      if (this.selectedCustomer?.id === id) {
        this.selectedCustomer = updated
      }
      return updated
    },

    async deleteCustomer(id: string) {
      await customerService.deleteCustomer(id)
      this.customers = this.customers.filter(c => c.id !== id)
      if (this.selectedCustomer?.id === id) {
        this.selectedCustomer = null
      }
    },

    async exportCustomers() {
      await customerService.exportCustomers()
    },

    setFilters(filters: CustomerFilters) {
      this.filters = filters
      this.pagination.page = 1
    },

    setSort(sortBy: string, sortOrder: SortOrder) {
      this.filters.sort_by = sortBy
      this.filters.sort_order = sortOrder
      this.pagination.page = 1
    },

    setPage(page: number) {
      this.pagination.page = page
    }
  }
})
